import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { AnnouncementModelLike } from 'app/shared/models/announcement.model';
import { ArcGisFeatureLike } from 'app/shared/models/arcgis-feature.model';
import { EntityModelLike } from 'app/shared/models/entity.model';
import {
  AnnouncementService,
  AnnouncementStatus,
  SearchAnnouncementFilterLike,
} from 'app/shared/services/announcement.service';
import { EntityService } from 'app/shared/services/entity.service';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { ToastService } from 'app/shared/services/toast.service';
import { HighlightService } from '../../highlight.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface SearchAnnouncementFormLike {
  status: FormControl<AnnouncementStatus>;
  idSiia: FormControl<string>;
  refentity: FormControl<string>;
  designation: FormControl<string>;
  entityId: FormControl<string>;
  startPublicationDate: FormControl<Date>;
  endPublicationDate: FormControl<Date>;
  startMembershipDeadlineDate: FormControl<Date>;
  endMembershipDeadlineDate: FormControl<Date>;
  startExecutionDate: FormControl<Date>;
  endExecutionDate: FormControl<Date>;
  interestActivated: FormControl<boolean>;
}

@Component({
  selector: 'app-search-announcement',
  templateUrl: 'search-announcement.component.html',
  styleUrls: ['search-announcement.component.scss'],
})
export class SearchAnnouncementComponent implements OnInit, OnDestroy {
  formGroup: FormGroup<SearchAnnouncementFormLike>;

  filterObj: SearchAnnouncementFilterLike;
  resultList: ArcGisFeatureLike<AnnouncementModelLike>[];

  statusList: CodedValueItem<number>[] = [
    {
      value: AnnouncementStatus.Running,
      label: 'Em execução',
    },
    {
      value: AnnouncementStatus.Finished,
      label: 'Terminada',
    },
  ];
  entityList: EntityModelLike[];

  totalCount: number;
  pageIndex: number = 0;
  pageSize: number = 5;

  modelZoomToSelected: ArcGisFeatureLike<AnnouncementModelLike>;
  private modelZoomToSelectedSubscription: Subscription;

  private pagesCache = new Map<
    number,
    ArcGisFeatureLike<AnnouncementModelLike>[]
  >();

  loadingController = new LoadingController();

  private searchResultsSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private announcementService: AnnouncementService,
    private entityService: EntityService,
    private mainMapService: MainMapService,
    private highlightService: HighlightService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group<SearchAnnouncementFormLike>({
      status: this.formBuilder.control<AnnouncementStatus>(null),
      idSiia: this.formBuilder.control<string>(null),
      refentity: this.formBuilder.control<string>(null),
      designation: this.formBuilder.control<string>(null),
      entityId: this.formBuilder.control<string>(null),
      startPublicationDate: this.formBuilder.control<Date>(null),
      endPublicationDate: this.formBuilder.control<Date>(null),
      startMembershipDeadlineDate: this.formBuilder.control<Date>(null),
      endMembershipDeadlineDate: this.formBuilder.control<Date>(null),
      startExecutionDate: this.formBuilder.control<Date>(null),
      endExecutionDate: this.formBuilder.control<Date>(null),
      interestActivated: this.formBuilder.control<boolean>(null),
    });
  }

  ngOnInit(): void {
    this.globalLoadingController.show();
    this.entityService
      .listAllEntities()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.entityList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-entities']
          );
        },
      });

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.formGroup.reset();

        this.filterObj = null;
        this.loadResults(true);
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onSubmit(e) {
    e?.preventDefault();

    this.filterObj = this.formGroup.getRawValue();

    this.loadResults(true);
  }

  onPageChange(pageIndex: number) {
    const lastPageIndex =
      Math.ceil((this.totalCount ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex =
      lastPageIndex <= -1
        ? -1
        : Math.max(0, Math.min(pageIndex, lastPageIndex));

    if (newPageIndex !== this.pageIndex) {
      this.pageIndex = newPageIndex;

      this.loadResults();
    }
  }

  onBtnZoomToClick(item: ArcGisFeatureLike<AnnouncementModelLike>) {
    if (item.geometry) {
      this.mainMapService.goTo(item.geometry);

      this.modelZoomToSelected = item;

      this.modelZoomToSelectedSubscription?.unsubscribe();
      this.modelZoomToSelectedSubscription = this.highlightService
        .highlight(item.geometry)
        .pipe(takeUntil(this.lifecycle))
        .subscribe();
    }
  }

  private loadResults(reloadMode = false) {
    this.searchResultsSubscription?.unsubscribe();
    if (reloadMode) {
      this.pageIndex = 0;
      this.totalCount = 0;
      this.resultList = null;
      this.pagesCache.clear();
    }

    if (!this.filterObj) {
      return;
    }

    if (this.pagesCache.has(this.pageIndex)) {
      this.resultList = this.pagesCache.get(this.pageIndex);
      return;
    }

    const skip = this.pageIndex * this.pageSize;

    this.loadingController.show();
    this.searchResultsSubscription = this.announcementService
      .queryList(this.filterObj, this.pageSize, skip)
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.resultList = res?.data;
          this.totalCount = res?.totalCount ?? this.resultList?.length;

          this.pagesCache.set(this.pageIndex, this.resultList);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-results']
          );
        },
      });
  }
}
